import React, { useState } from 'react';
import { ArrowRight, Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/utils';

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Waitlist signup against the `waitlist` table; a repeat email counts as joined. */
export function useWaitlist() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'joined'>('idle');

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const value = email.toLowerCase().trim();
    if (!EMAIL.test(value)) {
      toast.error('Enter a valid email address.');
      return;
    }
    setStatus('sending');
    try {
      const { error } = await supabase.from('waitlist').insert({ email: value });
      // 23505: already on the list, which is the outcome they wanted
      if (error && error.code !== '23505') throw error;
      setStatus('joined');
    } catch (err) {
      console.error('Waitlist signup error:', err);
      toast.error('Something went wrong. Try again in a moment.');
      setStatus('idle');
    }
  };

  return { email, setEmail, status, submit };
}

interface WaitlistFormProps {
  className?: string;
  /** Visible label for screen readers and the input's id. */
  id: string;
}

/**
 * One pill: the email field and the button live inside the same shape, like
 * the Partnr composer. Turns into a quiet confirmation once joined.
 */
export function WaitlistForm({ className, id }: WaitlistFormProps) {
  const { email, setEmail, status, submit } = useWaitlist();

  if (status === 'joined') {
    return (
      <div
        role="status"
        className={cn(
          'flex h-14 items-center gap-3 rounded-full border border-line bg-paper-raised pl-2 pr-6 text-left shadow-sm',
          'animate-in fade-in zoom-in-95 duration-300',
          className,
        )}
      >
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-ink text-paper-raised">
          <Check className="h-4 w-4" />
        </span>
        <span className="text-[15px] text-ink-2">You're on the list. We'll email you when there's room.</span>
      </div>
    );
  }

  const sending = status === 'sending';
  return (
    <form
      onSubmit={submit}
      className={cn(
        'group flex h-14 items-center gap-2 rounded-full border border-line bg-paper-raised p-1.5 pl-6 shadow-sm',
        'transition-shadow duration-200 ease-out focus-within:border-signal focus-within:shadow-[0_0_0_3px_hsl(var(--signal-wash))]',
        className,
      )}
    >
      <label htmlFor={id} className="sr-only">
        Email address
      </label>
      <input
        id={id}
        type="email"
        inputMode="email"
        autoComplete="email"
        placeholder="you@email.com"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        disabled={sending}
        className="min-w-0 flex-1 bg-transparent text-[15px] text-ink placeholder:text-ink-3 focus:outline-none"
      />
      <button
        type="submit"
        disabled={sending}
        className={cn(
          'flex h-full shrink-0 items-center gap-2 rounded-full bg-ink px-5 text-[15px] font-medium text-paper-raised',
          'transition-transform duration-150 ease-out active:scale-[0.97] disabled:opacity-70',
        )}
      >
        {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
        <span>{sending ? 'Joining' : 'Join the waitlist'}</span>
        {!sending && <ArrowRight className="hidden h-4 w-4 transition-transform duration-200 ease-out group-hover:translate-x-0.5 sm:block" />}
      </button>
    </form>
  );
}
